import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
    title: string;
    description: string;
    canonical?: string;
    keywords?: string;
    ogType?: string;
    ogImage?: string;
    noindex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
    title,
    description,
    canonical,
    keywords,
    ogType = 'website',
    ogImage = 'https://roadypacosferreira.com/assets/logo/roady-og-image.webp',
    noindex = false
}) => {
    const location = useLocation();
    const baseUrl = 'https://roadypacosferreira.com';
    const fullUrl = canonical || `${baseUrl}${location.pathname}`;

    useEffect(() => {
        // Set document title
        document.title = title;

        // Helper function to set or update meta tags
        const setMetaTag = (name: string, content: string, isProperty = false) => {
            const attribute = isProperty ? 'property' : 'name';
            let element = document.querySelector(`meta[${attribute}="${name}"]`);
            
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attribute, name);
                document.head.appendChild(element);
            }
            
            element.setAttribute('content', content);
        };

        // Set canonical link - remove ALL existing canonical links first, then set one
        const setCanonicalLink = (href: string) => {
            const existingCanonicals = document.querySelectorAll('link[rel="canonical"]');
            existingCanonicals.forEach((el, index) => {
                if (index > 0) el.remove(); // Remove duplicates, keep first
            });

            let element = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
            if (!element) {
                element = document.createElement('link');
                element.setAttribute('rel', 'canonical');
                document.head.appendChild(element);
            }
            element.href = href;
        };

        // Basic meta tags
        setMetaTag('description', description);
        if (keywords) {
            setMetaTag('keywords', keywords);
        }

        // Robots meta tag
        if (noindex) {
            setMetaTag('robots', 'noindex, nofollow');
        } else {
            setMetaTag('robots', 'index, follow');
        }

        // Canonical URL
        setCanonicalLink(fullUrl);

        // Open Graph tags
        setMetaTag('og:title', title, true);
        setMetaTag('og:description', description, true);
        setMetaTag('og:url', fullUrl, true);
        setMetaTag('og:type', ogType, true);
        setMetaTag('og:image', ogImage, true);
        setMetaTag('og:site_name', 'Roady Centro-Auto Paços de Ferreira', true);
        setMetaTag('og:locale', 'pt_PT', true);

        // Twitter Card tags
        setMetaTag('twitter:card', 'summary_large_image');
        setMetaTag('twitter:title', title);
        setMetaTag('twitter:description', description);
        setMetaTag('twitter:image', ogImage);

        // Additional SEO tags
        setMetaTag('author', 'Roady Centro-Auto Paços de Ferreira');
        setMetaTag('content-language', 'pt-PT');
        setMetaTag('geo.region', 'PT-13');
        setMetaTag('geo.placename', 'Paços de Ferreira');
        setMetaTag('geo.position', '41.283145;-8.386176');
        setMetaTag('ICBM', '41.283145, -8.386176');

    }, [title, description, fullUrl, keywords, ogType, ogImage, noindex]);

    return null;
};

export default SEO;

